import { useState, useEffect, createPortal } from "@wordpress/element";
import { Keyboard, X, RotateCcw } from "lucide-react";
import { formatShortcut } from "../hooks/useKeyboardShortcuts";

const SHORTCUT_DEFS = [
    { id: "save", label: "Save Settings", hint: "Save all current changes" },
    { id: "undo", label: "Undo", hint: "Step back one change" },
    { id: "redo", label: "Redo", hint: "Step forward one change" },
    {
        id: "search",
        label: "Open Search",
        hint: "Spotlight-style settings search",
    },
];

export default function ShortcutsModal({
    open,
    onClose,
    shortcuts,
    onUpdate,
    onReset,
}) {
    const [recording, setRecording] = useState(null);

    // Capture the next key combo while recording
    useEffect(() => {
        if (!recording) return;
        const onKey = (e) => {
            e.preventDefault();
            e.stopPropagation();
            if (e.key === "Escape") {
                setRecording(null);
                return;
            }
            if (["Control", "Meta", "Shift", "Alt"].includes(e.key)) return;
            onUpdate(recording, {
                key: e.key.toLowerCase(),
                ctrl: e.ctrlKey || e.metaKey,
                shift: e.shiftKey,
                alt: e.altKey,
            });
            setRecording(null);
        };
        window.addEventListener("keydown", onKey, { capture: true });
        return () =>
            window.removeEventListener("keydown", onKey, { capture: true });
    }, [recording, onUpdate]);

    useEffect(() => {
        if (!open) setRecording(null);
    }, [open]);

    if (!open) return null;

    const modal = (
        <div
            className="fixed inset-0 z-[99999] flex items-center justify-center"
            style={{
                background: "rgba(15,23,42,0.45)",
                backdropFilter: "blur(2px)",
            }}
            onMouseDown={(e) => {
                if (e.target === e.currentTarget) onClose();
            }}
        >
            <div
                className="w-full max-w-md overflow-hidden rounded-2xl bg-white shadow-2xl ring-1 ring-black/10"
                style={{
                    animation:
                        "wcpg-palette-in 0.18s cubic-bezier(0.32,0.72,0,1)",
                }}
            >
                {/* Header */}
                <div className="flex items-center justify-between border-b border-gray-100 px-5 py-4">
                    <div className="flex items-center gap-2.5">
                        <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-50">
                            <Keyboard className="h-4 w-4 text-blue-600" />
                        </div>
                        <div>
                            <p className="text-[13px] font-semibold text-gray-800">
                                Keyboard Shortcuts
                            </p>
                            <p className="text-[11px] text-gray-400">
                                Click a shortcut badge to reassign it
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="flex h-7 w-7 items-center justify-center rounded-lg text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
                    >
                        <X className="h-3.5 w-3.5" />
                    </button>
                </div>

                {/* Shortcut rows */}
                <div className="divide-y divide-gray-50 px-5 py-1">
                    {SHORTCUT_DEFS.map(({ id, label, hint }) => {
                        const sc = shortcuts[id];
                        const isRec = recording === id;
                        return (
                            <div
                                key={id}
                                className="flex items-center justify-between py-3.5"
                            >
                                <div>
                                    <p className="text-[13px] font-medium text-gray-800">
                                        {label}
                                    </p>
                                    <p className="text-[11px] text-gray-400">
                                        {hint}
                                    </p>
                                </div>
                                <button
                                    onClick={() =>
                                        setRecording(isRec ? null : id)
                                    }
                                    className={[
                                        "min-w-[96px] rounded-lg border px-3 py-1.5 text-center text-[12px] font-medium transition-all duration-150",
                                        isRec
                                            ? "animate-pulse border-blue-400 bg-blue-50 text-blue-600 ring-2 ring-blue-500/20"
                                            : "border-gray-200 bg-gray-50 text-gray-700 hover:border-gray-300 hover:bg-white",
                                    ].join(" ")}
                                >
                                    {isRec
                                        ? "Press keys…"
                                        : sc
                                        ? formatShortcut(sc)
                                        : "—"}
                                </button>
                            </div>
                        );
                    })}
                </div>

                {/* Footer */}
                <div className="flex items-center justify-between border-t border-gray-100 bg-gray-50/70 px-5 py-3">
                    <p className="text-[11px] text-gray-400">
                        Press{" "}
                        <kbd className="rounded border border-gray-200 bg-white px-1 text-[10px]">
                            Esc
                        </kbd>{" "}
                        to cancel recording
                    </p>
                    <button
                        onClick={onReset}
                        className="flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-[11px] font-medium text-gray-600 transition-colors hover:border-gray-300 hover:bg-gray-50"
                    >
                        <RotateCcw className="h-3 w-3" />
                        Reset defaults
                    </button>
                </div>
            </div>
        </div>
    );

    const portalTarget =
        document.getElementById("wpbean-pgs-setting-rule-app") || document.body;
    return createPortal(modal, portalTarget);
}
